import Reveal from "./Reveal";
import { Cpu, RadioTower, Database, Leaf, Check } from "lucide-react";

const techItems = [
  {
    icon: Cpu,
    title: "AI-Based Batching Systems",
    desc: "Smart algorithms that monitor real-time mix ratios, ensuring precision on every pour.",
  },
  {
    icon: RadioTower,
    title: "IoT Integration",
    desc: "Live tracking of fleet, plant status, and delivery progress via NavIC integration.",
  },
  {
    icon: Database,
    title: "ERP Automation",
    desc: "End-to-end operational control — from order management to invoicing, fully automated.",
  },
];

const sustainabilityItems = [
  "Green Concrete Production",
  "Energy Efficiency",
  "Low Carbon Emissions",
  "Grey Water Recycling",
];

const Technology = () => (
  <section className="w-full py-12 md:py-16 lg:py-20 px-4 md:px-10 lg:px-16 bg-[#fdf5ff]">
    <div className="max-w-7xl mx-auto">
      {/* Section Header */}
      <Reveal className="text-center mb-10 md:mb-12">
        <span className="text-xs font-semibold tracking-[0.25em] uppercase text-[#B3129E]">
          [ TECHNOLOGY & INNOVATION ]
        </span>

        <h2 className="text-2xl md:text-3xl lg:text-4xl xl:text-5xl tracking-wider text-[#041122] font-semibold font-heading leading-relaxed mt-3">
          Transforming Concrete Production with Tech
        </h2>
      </Reveal>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 items-stretch">
        {/* LEFT — Sustainability Panel */}
        <Reveal className="h-full">
          <div className="relative h-full rounded-3xl p-8 md:p-10 text-white overflow-hidden bg-gradient-to-br from-[#041122] via-[#2a0535] to-[#B3129E]">
            {/* subtle pattern */}
            <div
              className="absolute inset-0 opacity-10"
              style={{
                backgroundImage:
                  "radial-gradient(circle, white 1px, transparent 1px)",
                backgroundSize: "26px 26px",
              }}
            />

            <div className="relative z-10 flex flex-col justify-between h-full">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <Leaf size={26} className="text-white" />
                  <h3 className="font-heading font-semibold text-xl">
                    Building with Sustainability
                  </h3>
                </div>

                <p className="text-white/80 text-sm leading-relaxed mb-8 max-w-md">
                  Our commitment to the environment is baked into every batch.
                  From grey water recycling to low-emission vehicles,
                  sustainability is not a feature — it’s our foundation.
                </p>
              </div>

              <div className="space-y-3">
                {sustainabilityItems.map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">
                      <Check size={14} strokeWidth={3} />
                    </div>

                    <span className="text-white/90 text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Reveal>

        {/* RIGHT — Technology Cards */}
        <div className="grid grid-cols-1 gap-4">
          {techItems.map((t, i) => {
            const Icon = t.icon;

            return (
              <Reveal key={t.title} delay={i * 80}>
                <div className="group h-full rounded-2xl p-6 border border-[#ececf2] bg-white transition-all duration-300 hover:-translate-y-[3px] hover:shadow-[0_10px_28px_rgba(179,18,158,0.12)]">
                  <div className="flex gap-4">
                    {/* icon */}
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-gradient-to-br from-[#B3129E] to-[#8F0F7D] text-white flex-shrink-0">
                      <Icon size={18} strokeWidth={2} />
                    </div>

                    <div>
                      <h4 className="font-bold text-[#041122] text-sm mb-2">
                        {t.title}
                      </h4>

                      <p className="text-descriptionText text-xs leading-relaxed">
                        {t.desc}
                      </p>
                    </div>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </div>
  </section>
);

export default Technology;
